import { useEffect, useRef } from 'react'

type UseChatViewportScrollParams = {
  open: boolean
  isPageVariant: boolean
  isMobile: boolean
  dependencyValues: readonly unknown[]
}

export function useChatViewportScroll({
  open,
  isPageVariant,
  isMobile,
  dependencyValues
}: UseChatViewportScrollParams) {
  const endRef = useRef<HTMLDivElement | null>(null)

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
  }

  const scrollViewportToBottom = () => {
    if (!isPageVariant || !isMobile) {
      return
    }

    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'auto' })
  }

  useEffect(() => {
    if (!open) {
      return
    }

    let secondAnimationFrameId: number | null = null

    const animationFrameId = requestAnimationFrame(() => {
      scrollToBottom()
      scrollViewportToBottom()

      secondAnimationFrameId = requestAnimationFrame(() => {
        scrollToBottom()
        scrollViewportToBottom()
      })
    })

    return () => {
      cancelAnimationFrame(animationFrameId)

      if (secondAnimationFrameId !== null) {
        cancelAnimationFrame(secondAnimationFrameId)
      }
    }
  }, [open, isMobile, isPageVariant, ...dependencyValues])

  useEffect(() => {
    if (!open || !isPageVariant || !isMobile) {
      return
    }

    const viewport = window.visualViewport

    if (!viewport) {
      return
    }

    const handleViewportResize = () => {
      requestAnimationFrame(() => {
        scrollToBottom()
        scrollViewportToBottom()
      })
    }

    viewport.addEventListener('resize', handleViewportResize)

    return () => {
      viewport.removeEventListener('resize', handleViewportResize)
    }
  }, [open, isPageVariant, isMobile])

  return {
    endRef,
    scrollToBottom,
    scrollViewportToBottom
  }
}
